<template>
  <sheet-modal :model-value="modelValue" :title="title" exact-width="w-[560px]" body-class="px-8 pt-4 pb-6" scrollable
               data-testid="overlay-link-modal" @update:model-value="emit('update:modelValue', $event)">
    <!-- One block, so the modal's own gap between children doesn't add to the spacing here. -->
    <div>
      <p class="mb-4 text-[15px] leading-[22px] text-text-neutral-medium">{{ intro }}</p>

      <div class="mb-1.5 text-[14px] leading-[20px] font-medium text-text-neutral-high">{{ languageLabel }}</div>
      <!-- The app's usual language list, dialects included. -->
      <language-select v-model="selectedLanguage" show-flags class="mb-4" data-testid="overlay-language" />

      <!-- The OBS canvas shape: part of the session config, so it changes for everyone at once. -->
      <div class="mb-1.5 text-[14px] leading-[20px] font-medium text-text-neutral-high">{{ formatLabel }}</div>
      <div class="flex flex-wrap gap-2 mb-4" role="radiogroup" :aria-label="formatLabel">
        <button
            v-for="option in formatOptions"
            :key="option"
            role="radio"
            :aria-checked="format === option"
            class="flex items-center gap-1.5 h-9 px-3 rounded-[10px] text-[14px] leading-[20px] font-medium text-text-neutral-high cursor-pointer hover:bg-button-secondary-mono-active"
            :class="format === option ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
            :data-testid="`overlay-format-${option}`"
            @click="emit('format', option)"
        >
          <svg v-if="format === option" class="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" :d="CHECK_ICON_PATH" fill="currentColor" />
          </svg>
          {{ option }}
        </button>
      </div>

      <label class="block mb-1.5 text-[14px] leading-[20px] font-medium text-text-neutral-high" for="overlay_link">{{ linkLabel }}</label>
      <div class="flex gap-2 mb-1.5">
        <input id="overlay_link" :value="link" readonly data-testid="overlay-link"
               class="flex-1 min-w-0 h-11 px-3 rounded-[10px] border-2 border-[#292F36] bg-transparent text-text-neutral-high text-[14px] outline-none focus:border-[#6CBFFF]"
               @focus="$event.target.select()" />
        <button class="shrink-0 h-11 px-4 rounded-[10px] bg-primary text-black text-[15px] font-semibold cursor-pointer" data-testid="overlay-link-copy" @click="copy">
          {{ copied ? copiedLabel : copyLabel }}
        </button>
      </div>
      <p class="mb-4 text-[13px] leading-[18px] text-text-neutral-low">{{ snapshotNote }}</p>

      <div class="mb-4 flex items-center gap-2 text-[14px] leading-[20px]" data-testid="overlay-status">
        <span class="w-2 h-2 rounded-full" :class="connectedCount > 0 ? 'bg-[#51DBCD]' : 'bg-white/30'"></span>
        <span :class="connectedCount > 0 ? 'text-text-neutral-high' : 'text-text-neutral-low'">{{ statusText }}</span>
      </div>

      <h3 class="mb-1.5 text-[12px] leading-[16px] font-semibold uppercase tracking-wider text-text-neutral-low">{{ stepsTitle }}</h3>
      <ol class="list-decimal pl-5 space-y-0.5 text-[14px] leading-[20px] text-text-neutral-medium">
        <li v-for="step in steps" :key="step">{{ step }}</li>
      </ol>
    </div>
  </sheet-modal>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import SheetModal from '@/components/ui/SheetModal.vue';
import LanguageSelect from '@/components/ui/LanguageSelect.vue';
import { translate } from '@/composables/useCaptionSettings';
import { CANVAS_FORMATS, canvasFor, encodeConfigParam } from '@/services/greenScreenSync';
import { CHECK_ICON_PATH } from '@/composables/useGreenScreenBar';

const props = defineProps({
  modelValue: Boolean,
  roomCode: {
    type: String,
    required: true,
  },
  // The language the overlay's captions start in: the host's own.
  language: {
    type: String,
    required: false,
    default: '',
  },
  // The current Green Screen config, carried in the link for when no host is online.
  config: {
    type: Object,
    required: true,
  },
  // Key of CANVAS_FORMATS.
  format: {
    type: String,
    required: true,
  },
  connectedCount: {
    type: Number,
    required: false,
    default: 0,
  },
})

const emit = defineEmits(['update:modelValue', 'format'])

const title = computed(() => translate('overlay_link_title', 'OBS overlay'))
const intro = computed(() => translate('overlay_link_intro', 'Shows these Green Screen captions on a transparent background in OBS or vMix, with no chroma key. Changes you make here appear in the overlay right away.'))
const languageLabel = computed(() => translate('overlay_link_language', 'Captions language'))
const formatLabel = computed(() => translate('overlay_link_format', 'Canvas format'))
const formatOptions = Object.keys(CANVAS_FORMATS)
const linkLabel = computed(() => translate('overlay_link_link', 'Overlay link'))
const copyLabel = computed(() => translate('common_copy', 'Copy'))
const copiedLabel = computed(() => translate('common_copied', 'Copied'))
const snapshotNote = computed(() => translate('overlay_link_snapshot_note', 'The link also carries the current settings, used until a host is in the session.'))
const stepsTitle = computed(() => translate('overlay_link_steps_title', 'In OBS'))
const steps = computed(() => [
  translate('overlay_link_step_1', 'Sources → + → Browser.'),
  // The source size that matches the canvas format, e.g. 1080×1920 for 9:16.
  translate('overlay_link_step_2', 'Paste the link as the URL, set Width {width} and Height {height}.')
    .replace('{width}', canvasFor(props.format).width)
    .replace('{height}', canvasFor(props.format).height),
  translate('overlay_link_step_3', 'Put the source above your video. Repeat with another language for bilingual captions.'),
])
const statusText = computed(() => props.connectedCount > 0
  ? `${translate('overlay_link_connected', 'Overlays connected')}: ${props.connectedCount}`
  : translate('overlay_link_none_connected', 'No overlay connected yet'))

const selectedLanguage = ref(props.language)
watch(() => props.language, (value) => {
  if (!selectedLanguage.value) selectedLanguage.value = value
})

const link = computed(() => {
  const url = new URL(`/room/${encodeURIComponent(props.roomCode)}/overlay`, window.location.origin)
  if (selectedLanguage.value) {
    url.searchParams.set('lang', selectedLanguage.value)
  }
  url.searchParams.set('cfg', encodeConfigParam(props.config))
  return url.toString()
})

const copied = ref(false)
let copiedTimer = null
const copy = async () => {
  try {
    await navigator.clipboard.writeText(link.value)
    copied.value = true
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (e) {
    // No clipboard access (e.g. insecure context): the field is selectable to copy by hand.
    document.getElementById('overlay_link')?.select()
  }
}
</script>
